import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Card, Tag, Modal } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdvSet } from '../../types/adv';
import { advSetService } from '../../services/advSetService';
import AdvSetForm from './AdvSetForm';
import AdvSetManagement from './AdvSetManagement';

const { Title } = Typography;

const AdvSetsPage: React.FC = () => {
  const { t } = useTranslation();
  const [sets, setSets] = useState<AdvSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [managementVisible, setManagementVisible] = useState(false);
  const [selectedSet, setSelectedSet] = useState<AdvSet | undefined>(undefined);

  // Function to fetch all advertisement sets
  const fetchSets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await advSetService.getAllAdvSets();
      setSets(data);
    } catch (error) {
      console.error('Error fetching advertisement sets:', error);
      message.error(t('advertising.sets.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load sets on component mount
  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  // Handle editing a set
  const handleEdit = (set: AdvSet) => {
    setSelectedSet(set);
    setFormVisible(true);
  };

  // Handle managing a set (add/remove ads)
  const handleManage = (set: AdvSet) => {
    setSelectedSet(set);
    setManagementVisible(true);
  };

  // Handle creating a new set
  const handleCreate = () => {
    setSelectedSet(undefined);
    setFormVisible(true);
  };

  // Handle deleting a set
  const handleDelete = async (id: number) => {
    try {
      await advSetService.deleteAdvSet(id);
      message.success(t('advertising.sets.notifications.delete_success'));
      fetchSets();
    } catch (error) {
      console.error('Error deleting advertisement set:', error);
      message.error(t('advertising.sets.notifications.error', { message: (error as Error).message }));
    }
  };

  // After successful form submission, refresh the list
  const handleFormSuccess = () => {
    fetchSets();
  };

  // Table columns
  const columns = [
    {
      title: t('advertising.sets.list.name'),
      dataIndex: 'adv_set_name',
      key: 'adv_set_name',
    },
    {
      title: t('advertising.sets.list.status'),
      dataIndex: 'adv_set_active',
      key: 'adv_set_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? t('advertising.sets.list.active') : t('advertising.sets.list.inactive')}
        </Tag>
      )
    },
    {
      title: t('advertising.sets.list.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('advertising.sets.list.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('advertising.sets.list.actions'),
      key: 'actions',
      render: (_: any, record: AdvSet) => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => handleManage(record)}
            size="small"
          >
            {t('advertising.sets.actions.manage')} ({record.adv_count || 0})
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('advertising.sets.actions.edit')}
          </Button>
          <Popconfirm
            title={t('advertising.sets.actions.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              {t('advertising.sets.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('advertising.sets.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {t('advertising.sets.actions.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={sets}
        rowKey="id"
        loading={loading}
      />

      <AdvSetForm
        set={selectedSet}
        isVisible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />

      <AdvSetManagement
        set={selectedSet}
        isVisible={managementVisible}
        onClose={() => setManagementVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </Card>
  );
};

export default AdvSetsPage; 